import { startCase, toLower } from 'lodash-es'

import { appMessage } from 'utils/formatting'
import { updateObject } from 'app-redux/utility'
import * as actionTypes from 'app-redux/actionTypes'

const OTHER_APPS_NAME = 'other_apps'

const initialState = {
  // menuLeftData = tampilan akhir (baseDashboard yang sudah dikelompokkan + menuTail).
  menuLeftData: [],
  // baseDashboard = dashboard "rata" (semua app di level teratas) sbg sumber untuk regrouping.
  baseDashboard: [],
  // menuTail = bagian setelah dashboard (Settings, FAQ) yang tidak ikut dikelompokkan.
  menuTail: [],
  // groupMeta = judul/ikon/key node grup "Aplikasi Lainnya" (dari BE bila ada, atau fallback).
  groupMeta: null,
  sidebarApps: { available_apps: [], grouped_apps: [] },
}

// Memastikan format title huruf depan besar
const formatedTitle = (title) => startCase(toLower(title))

// Bangun satu node menu dashboard secara rekursif supaya mendukung >2 level
// (mis. grup "Aplikasi Lainnya" -> app -> submenu app). `depth` menentukan format judul:
// level teratas pakai judul apa adanya, level lebih dalam di-Title-case (spt sebelumnya).
const formatedDashboardItem = (item, keyPath, depth) => {
  const node = {
    key: `${item.title}-${keyPath}`,
    title: depth === 0 ? item.title : formatedTitle(item.title),
    name: item.name,
    icon: item.icon,
  }

  if (item.divider) {
    node.divider = item.divider
  }

  if (item.sub) {
    node.children = item.sub.map((row, rowIndex) =>
      formatedDashboardItem(row, `${keyPath}-${rowIndex}`, depth + 1),
    )
  } else {
    node.url = item.url
  }

  return node
}

const formatedDashboardMenu = (dashboardMenus) => {
  return dashboardMenus.map((item, itemIndex) => formatedDashboardItem(item, `${itemIndex}`, 0))
}

const formatedSettingMenu = (settingMenus) => {
  return settingMenus.map((item, itemIndex) => {
    const subMenu = {
      key: `${item.title}-${itemIndex}`,
      title: item.title,
      name: item.name,
      icon: item.icon,
      group_id: item.group_id,
    }

    if (item.divider) {
      subMenu.divider = item.divider
    }

    if (item.children) {
      subMenu.children = item.children.map((row, rowIndex) => {
        const subChildrenMenu = {
          key: `${row.title}-${rowIndex}`,
          title: formatedTitle(row.title),
          name: row.name,
          url: `/settings${row.url}`,
          icon: row.icon,
          group_id: row.group_id,
        }

        if (row.divider) {
          subChildrenMenu.divider = row.divider
        }

        return subChildrenMenu
      })
    } else if (!subMenu.divider) {
      subMenu.url = `/settings${item.url}`
    }

    return subMenu
  })
}
// Bagian menu setelah dashboard (Settings + FAQ) — tidak ikut dikelompokkan.
const buildMenuTail = (menus) => {
  const tail = [
    {
      title: appMessage('settings'),
      name: 'settings',
      key: 'Settings',
      icon: 'cogs',
      children: formatedSettingMenu(menus.settings),
      url: '/settings',
    },
  ]
  if (menus.faq) {
    menus.faq.key = menus.faq?.name
    tail.push(menus.faq)
  }
  return tail
}

// Judul/ikon/key grup "Aplikasi Lainnya": dari node BE bila ada, atau fallback lokal.
const extractGroupMeta = (dashboard) => {
  const group = dashboard.find((item) => item.name === OTHER_APPS_NAME)
  return {
    key: 'other_apps-group',
    title: group?.title || appMessage('menu.other_apps'),
    icon: group?.icon || 'th-large',
  }
}

// Ratakan dashboard: kembalikan anak node grup other_apps ke level teratas (di posisi grup).
const ungroupDashboard = (dashboard) =>
  dashboard.reduce((acc, item) => {
    if (item.name === OTHER_APPS_NAME && Array.isArray(item.children)) {
      return acc.concat(item.children)
    }
    acc.push(item)
    return acc
  }, [])

// Apakah node cocok dgn token app. Fallback membership: name node dashboard kadang beda dgn token
// available_apps ('membership') — cocokkan lewat judul.
const nodeMatchesApp = (node, app) => {
  const name = (node.name || '').toLowerCase()
  if (name === app) {
    return true
  }
  if (app === 'membership') {
    const title = (node.title || '').toLowerCase()
    return title.includes('membership') || title.includes('keanggotaan')
  }
  return false
}

// Bentuk grup other_apps dari baseDashboard (rata) + daftar grouped_apps (case-insensitive).
// Grup di-anchor pada AWAL blok aplikasi (app pertama yang bisa di-toggle / available_apps), bukan
// pada app ter-grup pertama — supaya posisi "Aplikasi Lainnya" STABIL & konsisten (sama dgn urutan
// BE) saat user menyalakan/mematikan app; grup tidak melompat-lompat. Anak grup & app on tetap
// mengikuti urutan baseDashboard (urutan dari BE). Fallback ke app ter-grup pertama bila
// available_apps kosong (mis. payload lama tanpa sidebar_apps).
const groupDashboard = (base, groupedApps, groupMeta, availableApps = []) => {
  const grouped = (groupedApps || []).map((app) => (app || '').toLowerCase()).filter(Boolean)
  if (grouped.length === 0) {
    return base
  }
  const isGrouped = (node) => grouped.some((app) => nodeMatchesApp(node, app))
  const available = (availableApps || []).map((app) => (app || '').toLowerCase()).filter(Boolean)
  const isAvailable = (node) => available.some((app) => nodeMatchesApp(node, app))

  let anchorIndex = available.length ? base.findIndex(isAvailable) : -1
  if (anchorIndex === -1) {
    anchorIndex = base.findIndex(isGrouped)
  }
  if (anchorIndex === -1) {
    return base
  }
  const groupNode = {
    key: groupMeta.key,
    name: OTHER_APPS_NAME,
    title: groupMeta.title,
    icon: groupMeta.icon,
    children: base.filter(isGrouped),
  }
  const out = []
  base.forEach((node, idx) => {
    if (idx === anchorIndex) {
      out.push(groupNode)
    }
    if (!isGrouped(node)) {
      out.push(node)
    }
  })
  return out
}

const computeMenuLeftData = (base, groupedApps, groupMeta, tail, availableApps) => [
  ...groupDashboard(base, groupedApps, groupMeta, availableApps),
  ...tail,
]

// Terapkan override judul (by name) rekursif — dipakai untuk rename/translate menu.
const applyTitleByName = (nodes, name, value) =>
  nodes.map((node) => {
    const next = node.name === name ? { ...node, title: value } : { ...node }
    if (Array.isArray(next.children)) {
      next.children = applyTitleByName(next.children, name, value)
    }
    return next
  })

const applyTitleMap = (nodes, map) =>
  nodes.map((node) => {
    const next = map[node.name] !== undefined ? { ...node, title: map[node.name] } : { ...node }
    if (Array.isArray(next.children)) {
      next.children = applyTitleMap(next.children, map)
    }
    return next
  })

const setMenu = (state, { payload }) => {
  const formattedDashboard = formatedDashboardMenu(payload.dashboard || [])
  const groupMeta = extractGroupMeta(formattedDashboard)
  const baseDashboard = ungroupDashboard(formattedDashboard)
  const menuTail = buildMenuTail(payload)
  const sidebarApps = payload.sidebar_apps || { available_apps: [], grouped_apps: [] }

  return updateObject(state, {
    baseDashboard,
    menuTail,
    groupMeta,
    sidebarApps,
    menuLeftData: computeMenuLeftData(
      baseDashboard,
      sidebarApps.grouped_apps,
      groupMeta,
      menuTail,
      sidebarApps.available_apps,
    ),
  })
}

// Toggle bintang: ubah grouped_apps -> menu langsung tersusun ulang dari baseDashboard (tanpa reload).
const setGroupedApps = (state, { payload }) => {
  const sidebarApps = { ...state.sidebarApps, grouped_apps: payload }
  return updateObject(state, {
    sidebarApps,
    menuLeftData: computeMenuLeftData(
      state.baseDashboard,
      payload,
      state.groupMeta,
      state.menuTail,
      state.sidebarApps.available_apps,
    ),
  })
}

const editMenu = (state, { payload }) => {
  const baseDashboard = applyTitleByName(state.baseDashboard, payload.name, payload.value)
  const menuTail = applyTitleByName(state.menuTail, payload.name, payload.value)
  return updateObject(state, {
    baseDashboard,
    menuTail,
    menuLeftData: computeMenuLeftData(
      baseDashboard,
      state.sidebarApps.grouped_apps,
      state.groupMeta,
      menuTail,
      state.sidebarApps.available_apps,
    ),
  })
}

const resetMenu = (state, { payload }) => {
  const baseDashboard = applyTitleMap(state.baseDashboard, payload)
  const menuTail = applyTitleMap(state.menuTail, payload)
  return updateObject(state, {
    baseDashboard,
    menuTail,
    menuLeftData: computeMenuLeftData(
      baseDashboard,
      state.sidebarApps.grouped_apps,
      state.groupMeta,
      menuTail,
      state.sidebarApps.available_apps,
    ),
  })
}

export default function menuReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.SET_MENU:
      return setMenu(state, action)
    case actionTypes.SET_GROUPED_APPS:
      return setGroupedApps(state, action)
    case actionTypes.EDIT_MENU:
      return editMenu(state, action)
    case actionTypes.RESET_MENU:
      return resetMenu(state, action)
    default:
      return state
  }
}
